import { useTable } from '@aibees/shared/components';
import { useSearchParamsSource } from '@aibees/shared/hooks';
import { toNumber } from '@aibees/shared/utils';
import {
  getCoreRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { isEqual } from 'lodash-es';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useBrowseApiDataCore } from '../components/table/hooks/use-get-browse-api-data';
import { adaptColumns, buildColumnVisibility } from '../utils/adapt-columns';
import { appendActionColumnToConfig } from '../utils';
import type {
  AdvanceTableConfigContextValue,
  AdvanceTableContextContextData as ContextData,
  AdvanceTableContextContextProps as ContextProps,
  AdvanceTableDataBrowseContextValue,
  AdvanceTableDataContextValue,
  AdvanceTableDataFiltersContextValue,
  AdvanceTableStickyContextValue,
  AdvanceTableTableContextValue,
} from './types';

const ConfigContext = createContext<AdvanceTableConfigContextValue>(
  {} as AdvanceTableConfigContextValue
);
const TableContext = createContext<AdvanceTableTableContextValue>(
  {} as AdvanceTableTableContextValue
);
const DataFiltersContext = createContext<AdvanceTableDataFiltersContextValue>(
  {} as AdvanceTableDataFiltersContextValue
);
const DataBrowseContext = createContext<AdvanceTableDataBrowseContextValue>(
  {} as AdvanceTableDataBrowseContextValue
);
const StickyContext = createContext<AdvanceTableStickyContextValue>(
  {} as AdvanceTableStickyContextValue
);

export const ConfigContextProvider = ConfigContext.Provider;
export const TableContextProvider = TableContext.Provider;
export const DataFiltersContextProvider = DataFiltersContext.Provider;
export const DataBrowseContextProvider = DataBrowseContext.Provider;
export const StickyContextProvider = StickyContext.Provider;

export function useAdvanceTableConfigContext() {
  return useContext(ConfigContext);
}

export function useAdvanceTableTableContext() {
  return useContext(TableContext);
}

export function useAdvanceTableDataFiltersContext() {
  return useContext(DataFiltersContext);
}

export function useAdvanceTableDataBrowseContext() {
  return useContext(DataBrowseContext);
}

/** Combined data context; re-renders when either filters or browse change. Prefer granular hooks when only one slice is needed. */
export function useAdvanceTableDataContext(): AdvanceTableDataContextValue {
  const filtersSlice = useContext(DataFiltersContext);
  const browseSlice = useContext(DataBrowseContext);
  return { ...filtersSlice, ...browseSlice };
}

export function useAdvanceTableStickyContext() {
  return useContext(StickyContext);
}

/** Returns the full context; use granular hooks above to avoid re-renders when only a slice changes. */
export function useAdvanceTableContext(): ContextData {
  return {
    ...useContext(ConfigContext),
    ...useContext(TableContext),
    ...useContext(DataFiltersContext),
    ...useContext(DataBrowseContext),
    ...useContext(StickyContext),
  };
}

export const AdvanceTableContextProvider = ({
  children,
  config: conf,
}: ContextProps) => {
  const [stickyRowHeight, setStickyRowHeight] = useState<number | null>(null);
  const [stickyRowsHeight, setStickyRowsHeight] = useState<number>(0);
  const [stickyCollapsed, setStickyCollapsed] = useState<boolean>(true);
  const { params = {}, setParams } = useSearchParamsSource();

  // Memoize config with appended action column
  const config = useMemo(() => appendActionColumnToConfig(conf), [conf]);

  // Legacy table hook (provides selection, dense, query-string sync, and the full TableProps shape)
  const table = useTable({
    defaultCurrentPage: toNumber(params.page) ?? 0,
    defaultRowsPerPage: conf.rowsPerPageOptions?.[0] ?? 5,
    defaultOrderBy: conf.defaultOrderBy,
  });

  // Initialize filters with query params and initialFilters; sync from params when params change (e.g. store/URL)
  const [filters, setFilters] = useState<ContextData['filters']>(() => ({
    ...(typeof conf?.initialFilters === 'function'
      ? conf.initialFilters()
      : conf?.initialFilters),
    ...params,
  }));

  // Sync filters from params; keep initialFilters as base so we don't drop config defaults (avoids double API call).
  // Only update when merged value actually changes so we don't overwrite a user change with stale params (which would revert the store and break undo/redo).
  useEffect(() => {
    const base =
      typeof conf?.initialFilters === 'function'
        ? conf.initialFilters()
        : conf?.initialFilters;
    const next = { ...base, ...params };
    setFilters((prev) => (isEqual(prev, next) ? prev : next));
  }, [params, conf]);

  // Fetch browse data once for the entire table tree.
  // Previously each sub-component (Table, Collapse, StickyRows, useTableHeight)
  // called useGetBrowseApiData independently, causing 4x duplicate hook invocations.
  // Passed directly to DataContext (no useDeferredValue) to avoid double Table re-renders
  // per API transition; filter responsiveness is preserved by the 100ms debounce in useBrowseApiDataCore.
  const browseData = useBrowseApiDataCore(config, filters, table);

  // --- TanStack React Table integration ---
  // Memoize column definitions converted from AdvanceTableColumn[] to ColumnDef[]
  const tanstackColumns = useMemo(
    () => adaptColumns(config.columns),
    [config.columns]
  );

  // Controlled sorting state derived from legacy table state
  const sorting = useMemo<SortingState>(
    () =>
      table.orderBy
        ? [{ id: table.orderBy, desc: table.order === 'desc' }]
        : [],
    [table.orderBy, table.order]
  );

  // Controlled column visibility from column definitions
  const columnVisibility = useMemo(
    () => buildColumnVisibility(config.columns),
    [config.columns]
  );

  // Data for the TanStack table: API content or empty array
  const tableData = useMemo(
    () => browseData.data?.content ?? [],
    [browseData.data?.content]
  );

  const totalElements = browseData.data?.totalElements ?? 0;

  const reactTableRaw = useReactTable({
    data: tableData,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    state: {
      sorting,
      pagination: {
        pageIndex: table.page,
        pageSize: table.rowsPerPage,
      },
      columnVisibility,
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      if (next.length > 0) {
        const col = next[0];
        table.setOrderBy(col.id);
        table.setOrder(col.desc ? 'desc' : 'asc');
      }
    },
    onPaginationChange: (updater) => {
      const prev = { pageIndex: table.page, pageSize: table.rowsPerPage };
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next.pageIndex !== prev.pageIndex) {
        table.setPage(next.pageIndex);
      }
      if (next.pageSize !== prev.pageSize) {
        table.setRowsPerPage(next.pageSize);
        table.setPage(0);
      }
    },
    pageCount: Math.ceil(totalElements / (table.rowsPerPage || 1)),
  });

  // Stabilize reactTable identity: useReactTable returns a new object every
  // render but its internal row model is memoized. Keep a ref and only swap
  // the context-visible instance when meaningful inputs actually change.
  const reactTableRef = useRef(reactTableRaw);
  reactTableRef.current = reactTableRaw;
  const reactTableVersion = useMemo(
    () => ({}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableData, sorting, columnVisibility, table.page, table.rowsPerPage]
  );
  const reactTable = useMemo(() => {
    return reactTableRef.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactTableVersion]);

  const paramsRef = useRef(params);
  const tableRef = useRef(table);
  paramsRef.current = params;
  tableRef.current = table;

  // Stable setFilter so dataContextValue doesn't change when only params/table change (reduces table re-renders)
  const setFilter = useCallback<ContextData['setFilter']>(
    (key, value) => {
      setFilters((prev) => ({ ...prev, page: 0, [key]: value }));
      setParams({
        ...paramsRef.current,
        page: '0',
        [key]: value != null ? String(value) : undefined,
      });
      tableRef.current.setPage(0);
    },
    [setFilters, setParams]
  );

  const clearFilters = useCallback<ContextData['clearFilters']>(
    () => setFilters({}),
    []
  );

  const configContextValue = useMemo(
    () => ({ config, maxStickyRows: conf.maxStickyRows }),
    [config, conf.maxStickyRows]
  );

  const tableContextValue = useMemo(() => ({ table }), [table]);

  const dataFiltersContextValue = useMemo(
    () => ({
      filters,
      setFilter,
      clearFilters,
    }),
    [filters, setFilter, clearFilters]
  );

  const dataBrowseContextValue = useMemo(
    () => ({
      browseData,
      reactTable,
    }),
    [browseData, reactTable]
  );

  const stickyContextValue = useMemo(
    () => ({
      stickyCollapsed,
      setStickyCollapsed,
      stickyRowsHeight,
      setStickyRowsHeight,
      stickyRowHeight,
      setStickyRowHeight,
    }),
    [
      stickyCollapsed,
      setStickyCollapsed,
      stickyRowsHeight,
      setStickyRowsHeight,
      stickyRowHeight,
      setStickyRowHeight,
    ]
  );

  return (
    <ConfigContextProvider value={configContextValue}>
      <TableContextProvider value={tableContextValue}>
        <DataFiltersContextProvider value={dataFiltersContextValue}>
          <DataBrowseContextProvider value={dataBrowseContextValue}>
            <StickyContextProvider value={stickyContextValue}>
              {children}
            </StickyContextProvider>
          </DataBrowseContextProvider>
        </DataFiltersContextProvider>
      </TableContextProvider>
    </ConfigContextProvider>
  );
};
